<script setup lang="ts">
import { dir } from '@@/bits/current_dir'
import { __ } from '@@/bits/intl'
import type { CarouselItem } from '@@/bits/landside_helper'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIconButton, { OzIconButtonColorScheme } from '@@/library/v4/components/OzIconButton.vue'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { VueCssClass } from '@@/types'
import { useCarousel } from '@@/vuecomposables/carousel'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    carouselItems: CarouselItem[]
    carouselName: string
    carouselAriaLabelSmallerThanDesktop: string
    isDarkMode: boolean
    xDescription?: boolean
    descriptionClasses?: VueCssClass | undefined
    ringOffsetColorClasses?: VueCssClass
  }>(),
  {
    xDescription: false,
    descriptionClasses: undefined,
    ringOffsetColorClasses: undefined,
  },
)

const { windowWidth, isSmallerThanDesktop, isSmallerThanTabletLandscape, isSmallerThanTabletPortrait } = storeToRefs(
  useWindowSizeStore(),
)

const carouselImageContainerRef = ref<HTMLElement>()
const carouselButtonContainerMobileRef = ref<HTMLElement>()
const carouselButtonMobileRefs = ref<HTMLElement[]>()

const carouselImageWidth = ref(1)
const carouselScrollFromButtonSelect = ref(false)
const isTimedScroll = ref(false)
const isActiveImageInFullView = ref(true)

const carouselPaddingX = computed<number>(() => {
  if (isSmallerThanTabletPortrait.value) {
    return 16
  } else if (isSmallerThanDesktop.value) {
    return 20
  } else {
    return 0
  }
})

const carouselGapBetweenSlides = computed<number>(() => {
  if (isSmallerThanTabletPortrait.value) {
    return 16
  } else if (isSmallerThanTabletLandscape.value) {
    return 24
  } else {
    return 32
  }
})

const onTransitionToAnotherSlide = () => {
  isTimedScroll.value = true
  isActiveImageInFullView.value = false
}

const {
  currentSlideIndex,
  startAutoplay,
  startAutoplayIfUnpaused,
  pauseAutoplay,
  toggleAutoplay,
  isAutoplaying,
  transitToNextSlide,
  transitToPreviousSlide,
} = useCarousel({
  slides: props.carouselItems,
  onTransitionToPreviousSlide: onTransitionToAnotherSlide,
  onTransitionToNextSlide: onTransitionToAnotherSlide,
})

const isAutoplayTriggeredManually = ref(false)
const toggleAutoplayFromUserInput = () => {
  isAutoplayTriggeredManually.value = true
  toggleAutoplay()
}

const pauseAutoplayIfAutoplayButtonIsNotClicked = () => {
  // We don't have autoplay on mobile so we don't need to pause it
  if (isSmallerThanDesktop.value) return

  if (!isAutoplayTriggeredManually.value) {
    pauseAutoplay()
  }
}

const resumeAutoplayIfAutoplayButtonIsNotClicked = () => {
  // We don't have autoplay on mobile so we don't need to resume it
  if (isSmallerThanDesktop.value) return

  if (!isAutoplayTriggeredManually.value) {
    startAutoplay()
  }
}

const updateCarouselImageWidth = () => {
  const carouselWidth = carouselImageContainerRef.value?.clientWidth ?? carouselImageWidth.value
  // Subtract the carousel width by the padding-left and the gap between the first and second slides
  const availableSpaceForSlides = carouselWidth - carouselPaddingX.value - carouselGapBetweenSlides.value

  if (isSmallerThanDesktop.value) {
    // In views smaller than desktop, the carousel's next slide peek is 1/6 of the slide width.
    // This means that the card width is 6/7 of the available space, and the next slide peek is 1/7 of the available space.
    carouselImageWidth.value = (availableSpaceForSlides / 7) * 6
  } else {
    carouselImageWidth.value = carouselWidth * 1
  }
}

const handleButtonClick = (index: number) => {
  currentSlideIndex.value = index
  isActiveImageInFullView.value = false
  carouselScrollFromButtonSelect.value = true
}

const handleUserManualScroll = (currentImageIndex: number) => {
  currentSlideIndex.value = currentImageIndex
}

const handleScrollEnd = (currentImageIndex: number) => {
  const reachedTargetItem = currentSlideIndex.value === currentImageIndex
  // Only remove flags if scrolled to target (and not any other image)
  if (reachedTargetItem) {
    carouselScrollFromButtonSelect.value = false
    isTimedScroll.value = false
    isActiveImageInFullView.value = true
  }
}

const imageScrollHandler = (e: Event) => {
  if (e.target === null) return
  isActiveImageInFullView.value = false

  const target = e.target as HTMLDivElement

  // Since image has width of `carouselImageWidth.value`.
  // if first image is in view, target.scrollLeft = 0, currentImageIndex = target.scrollLeft / carouselImageWidth.value = 0.
  // if second image is in view, target.scrollLeft = carouselImageWidth.value (one image width on the left), currentImageIndex = carouselImageWidth.value / carouselImageWidth.value = 1.
  // Using round so that the image with the most content in view is set as the active item
  // Using abs because scrollLeft is negative for RTL
  const currentImageIndex = Math.round(Math.abs(target.scrollLeft) / carouselImageWidth.value)

  // if scroll is from user manually scrolling
  if (!carouselScrollFromButtonSelect.value && !isTimedScroll.value) {
    handleUserManualScroll(currentImageIndex)
  }

  // scroll snap complete event, when scroll position is a multiple of image width
  // There's an edge case where certain viewports result in floating point width. using `divide and modulo 1` instead of just `modulo` to check if is multiple of image width
  // `RightImage` refers to image on the right (LTR) when there are 2 images in view
  const percentageOfRightImageInView = Math.abs(target.scrollLeft / carouselImageWidth.value) % 1
  // threshold chosen as a small number to account for calculation errors with floating points mentioned above,
  // and what looks visually alright when hiding the side images during scroll
  const leftThreshold = 0.005
  const rightThreshold = 0.995
  if (
    percentageOfRightImageInView < leftThreshold || // scrolling left snap
    percentageOfRightImageInView > rightThreshold // scrolling right snap
  ) {
    handleScrollEnd(currentImageIndex)
  }
}

const scrollActiveImageIntoView = () => {
  if (carouselImageContainerRef.value) {
    // total width of number of images left of active item
    let activeImageScrollLeft = currentSlideIndex.value * carouselImageWidth.value
    if (dir() === 'rtl') {
      activeImageScrollLeft *= -1
    }
    carouselImageContainerRef.value.scroll({
      left: activeImageScrollLeft,
      behavior: 'smooth',
    })
  }
}

const scrollActiveButtonIntoView = () => {
  // Scroll active button to the center
  if (carouselButtonMobileRefs.value && carouselButtonContainerMobileRef.value) {
    const buttonContainerWidth = carouselButtonContainerMobileRef.value.clientWidth
    const targetButtonWidth = carouselButtonMobileRefs.value[currentSlideIndex.value].clientWidth
    const targetPositionDistanceFromLeft = (buttonContainerWidth - targetButtonWidth) / 2
    const targetButtonOffsetLeft = carouselButtonMobileRefs.value[currentSlideIndex.value].offsetLeft
    const parentOffsetLeft = carouselButtonContainerMobileRef.value.offsetLeft
    const buttonScrollPosition = targetButtonOffsetLeft - parentOffsetLeft - targetPositionDistanceFromLeft
    carouselButtonContainerMobileRef.value.scroll({ left: buttonScrollPosition, behavior: 'smooth' })
  }
}

onMounted(() => {
  // start at first item
  nextTick(() => {
    if (carouselImageContainerRef.value) {
      carouselImageContainerRef.value.scrollLeft = 0
    }
  })

  setTimeout(() => {
    updateCarouselImageWidth()
  }, 0)

  carouselImageContainerRef.value?.addEventListener('scroll', imageScrollHandler)
  startAutoplayIfUnpaused()
})
onUnmounted(() => {
  carouselImageContainerRef.value?.removeEventListener('scroll', imageScrollHandler)
})

watch(windowWidth, updateCarouselImageWidth)

watch(
  isSmallerThanDesktop,
  () => {
    if (isSmallerThanDesktop.value) {
      pauseAutoplay()
    } else {
      startAutoplay()
    }
  },
  { immediate: true },
)

watch(currentSlideIndex, () => {
  // Scroll active image into view if user selects button or timed scroll
  if (carouselScrollFromButtonSelect.value || isTimedScroll.value) {
    scrollActiveImageIntoView()
  }

  scrollActiveButtonIntoView()
})
</script>

<template>
  <section
    :aria-roledescription="__('carousel')"
    :aria-labelledby="`${carouselName}-header`"
    class="w-full h-full flex flex-col py-1 desktop:p-1"
  >
    <p :id="`${carouselName}-carousel-label`" class="sr-only">
      {{
        isSmallerThanDesktop
          ? carouselAriaLabelSmallerThanDesktop
          : __('Carousel is at slide %{currentSlide} of %{numSlides}', {
              currentSlide: currentSlideIndex + 1,
              numSlides: carouselItems.length,
            })
      }}
    </p>
    <div class="relative group">
      <p v-if="!isSmallerThanDesktop" :id="`${carouselName}-carousel-description`" class="sr-only">
        {{
          isAutoplaying
            ? __('Carousel is autoplaying. Click to pause.')
            : __('Carousel is paused. Click to resume autoplaying.')
        }}
      </p>
      <!-- Slides -->
      <ul
        ref="carouselImageContainerRef"
        :class="[
          'w-full box-border flex overflow-x-scroll no-scrollbar desktop:rounded-3xl',
          {
            'pointer-events-none': carouselScrollFromButtonSelect,
          },
          // Focus
          'focus-visible:ring-2.5 focus-visible:ring-offset-[1.5px]',
          !isDarkMode && [
            'focus-visible:ring-grape-500',
            ringOffsetColorClasses || 'focus-visible:ring-offset-light-ui-100',
          ],
          isDarkMode && [
            'focus-visible:ring-canary-500',
            ringOffsetColorClasses || 'focus-visible:ring-offset-dark-ui-100',
          ],
        ]"
        :style="{
          scrollSnapType: 'x mandatory',
          gap: `${carouselGapBetweenSlides}px`,
          'padding-inline': `${carouselPaddingX}px`,
        }"
        :aria-labelledby="`${carouselName}-carousel-label`"
        :aria-describedby="!isSmallerThanDesktop ? `${carouselName}-carousel-description` : undefined"
        tabindex="0"
        @focusin="pauseAutoplayIfAutoplayButtonIsNotClicked"
        @focusout="resumeAutoplayIfAutoplayButtonIsNotClicked"
      >
        <!-- Carousel -->
        <template v-for="(carouselItem, index) in carouselItems">
          <li
            :key="index"
            class="flex flex-col"
            :style="{
              scrollSnapAlign: 'center',
              scrollSnapStop: 'always',
            }"
            :aria-roledescription="__('Slide')"
            :aria-labelledby="`${carouselName}-carousel-slide-title-${index}`"
          >
            <ImageThumbnail
              :src="carouselItem.image.url"
              :fallback-to-placeholder="true"
              :original-image-height="carouselItem.image.originalHeight"
              :original-image-width="carouselItem.image.originalWidth"
              :width="carouselImageWidth"
              :title="carouselItem.description"
              :aspect-ratio="16 / 9"
              :process-image="true"
              :class="[
                'rounded-t-xl tablet-landscape:rounded-t-3xl',
                !xDescription && 'rounded-b-xl tablet-landscape:rounded-b-3xl',
                isActiveImageInFullView && index !== currentSlideIndex && !isSmallerThanDesktop && 'invisible',
              ]"
              alt=""
            />
            <p
              :id="`${carouselName}-carousel-slide-title-${index}`"
              :class="[
                'rounded-b-xl tablet-landscape:rounded-b-3xl text-body grow desktop:grow-0 px-5 tablet-portrait:px-8 py-4 tablet-portrait:py-6',
                descriptionClasses,
                !xDescription && 'sr-only',
              ]"
            >
              {{ carouselItem.description }}
            </p>
          </li>
        </template>
      </ul>
    </div>
    <!-- Navigation control buttons -->
    <div v-if="!isSmallerThanDesktop" class="mt-6 mx-6 flex items-center gap-4" :aria-label="__('Slide controls')">
      <OzIconButton
        :color-scheme="OzIconButtonColorScheme.SecondaryClear"
        :icon-name="isAutoplaying ? 'pause_outline' : 'play_outline'"
        :icon-size="24"
        :label="isAutoplaying ? __('Pause autoplay') : __('Resume autoplay')"
        :dark-mode="isDarkMode"
        @click="toggleAutoplayFromUserInput"
      />
      <ul class="flex items-center flex-1 gap-1" :aria-label="__('Navigate to specific slides in the carousel')">
        <template v-for="(_, index) in carouselItems">
          <li :key="index" class="w-full list-none">
            <OzBaseButton
              :class="[
                'group w-full h-1.5 relative overflow-hidden',
                // Border radius
                index === 0 && 'rounded-s-[50px] rounded-e-sm',
                index === carouselItems.length - 1 && 'rounded-s-sm rounded-e-[50px]',
                index !== 0 && index !== carouselItems.length - 1 && 'rounded-xs',
                // Focus
                'focus-visible:ring-2.5 focus-visible:ring-offset-[1.5px]',
                !isDarkMode && [
                  // Focus
                  'focus-visible:ring-grape-500',
                  ringOffsetColorClasses || 'focus-visible:ring-offset-light-ui-100',
                  // Background color
                  index < currentSlideIndex && 'bg-grape-500',
                  index === currentSlideIndex && isAutoplaying && 'bg-button-light',
                  index === currentSlideIndex && !isAutoplaying && 'bg-grape-500',
                  index > currentSlideIndex && [
                    // Base
                    'bg-button-light',
                    // Hover
                    'hhover:bg-button-hover-light',
                    // Focus
                    'focus-visible:bg-button-hover-light',
                    // Active
                    'hactive:bg-grape-500',
                  ],
                ],
                isDarkMode && [
                  // Focus
                  'focus-visible:ring-canary-500',
                  ringOffsetColorClasses || 'focus-visible:ring-offset-dark-ui-100',
                  // Background color
                  index < currentSlideIndex && 'bg-canary-500',
                  index === currentSlideIndex && isAutoplaying && 'bg-button-dark',
                  index === currentSlideIndex && !isAutoplaying && 'bg-canary-500',
                  index > currentSlideIndex && [
                    // Base
                    'bg-button-dark',
                    // Hover
                    'hhover:bg-button-hover-dark',
                    // Focus
                    'focus-visible:bg-button-hover-dark',
                    // Active
                    'hactive:bg-canary-500',
                  ],
                ],
              ]"
              :aria-label="
                __('Navigate to slide %{slideNumber} of %{totalSlidesCount}', {
                  slideNumber: index + 1,
                  totalSlidesCount: carouselItems.length,
                })
              "
              @click="handleButtonClick(index)"
            >
              <div
                v-if="isAutoplaying && index === currentSlideIndex"
                :class="[
                  'absolute top-0 z-50 w-full h-1.5 animate-progress-indicator',
                  !isDarkMode ? 'bg-grape-500' : 'bg-canary-500',
                  dir() === 'ltr' ? 'origin-left' : 'origin-right',
                ]"
              />
            </OzBaseButton>
          </li>
        </template>
      </ul>
      <div role="group" class="flex items-center">
        <OzIconButton
          :color-scheme="OzIconButtonColorScheme.SecondaryClear"
          :icon-name="'chevron_left_center'"
          :icon-size="24"
          :label="__('Previous slide')"
          :dark-mode="isDarkMode"
          @click="transitToPreviousSlide"
        />
        <OzIconButton
          :color-scheme="OzIconButtonColorScheme.SecondaryClear"
          :icon-name="'chevron_right_center'"
          :icon-size="24"
          :label="__('Next slide')"
          :dark-mode="isDarkMode"
          @click="transitToNextSlide"
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
@keyframes progress-indicator {
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
}

.animate-progress-indicator {
  animation: progress-indicator 5s linear;
}
</style>
